import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Pagination,
  Stack,
  Tooltip
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Eye, PlusCircle, Trash01 } from 'untitledui-js-base';
import { UserResponse, useCommonApi } from '../api';
import { surfaceSx } from '../styles/tokens';
import { FlexBlock, PageLayout } from './common';
import { NoDataFound } from './common/NoDataFound';
import { useAuth } from './hooks';
import { useAvailableRentalCar } from './hooks/useAvailableRentalCar';
import { useRentalCar } from './hooks/useRentalCar';
import { RentalItem } from './rental';
import { RentFinishConfirmDialog } from './rental/RentFinishConfirmDialog';
import { RentRemoveConfirmDialog } from './rental/RentRemoveConfirmDialog';
import { RentStartConfirmDialog } from './rental/RentStartConfirmDialog';
import { RentalCarSelectionModal } from './rental/RentalCarSelectionModal';
import { RentalFiltersType, RentalsFilters } from './rental/RentalsFilters';
import { Role } from './types';
import { RegistryType } from './types/accident';
import { CarRental, CarRentalStatus } from './types/rental';

const RENTALS_LIMIT = 10;

interface RentalsListProps {
  rentals: CarRental[];
  detailPath: (rentalId: string) => string;
  onRemove?: (id: number) => void;
  onFinish?: (id: number) => void;
  onStart?: (id: number) => void;
}

const RentalsList = ({ rentals, detailPath, onRemove, onFinish, onStart }: RentalsListProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <Stack gap={2}>
      <List sx={{ ...surfaceSx, width: 1, padding: 0, overflow: 'hidden' }}>
        {rentals?.map((rental, idx) => {
          const rentalDetailPath = detailPath(rental.id.toString());
          return (
            <React.Fragment key={rental.id}>
              <ListItem
                sx={{
                  paddingX: { xs: 2, md: 3 },
                  paddingY: 2,
                  width: 1,
                  '&:hover': { backgroundColor: 'rgba(34, 34, 52, 0.025)' }
                }}
              >
                <RentalItem
                  onClick={() => navigate(rentalDetailPath)}
                  {...rental}
                  extra={
                    <Stack direction="row" gap={1} justifyContent={{ md: 'end' }}>
                      <Tooltip title="Zobrazit detail" placement="top">
                        <IconButton color="info" onClick={() => navigate(rentalDetailPath)}>
                          <Eye />
                        </IconButton>
                      </Tooltip>
                      {!rental.isReturned && user?.role === Role.Dealer && (
                        <>
                          {rental.isRunning && (
                            <Tooltip title="Dokončit zápůjčku" placement="top">
                              <IconButton color="success" onClick={() => onFinish?.(rental.id)}>
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Zrušit zápůjčku" placement="top" onClick={() => onRemove?.(rental.id)}>
                            <IconButton color="error">
                              <Trash01 />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  }
                />
              </ListItem>
              {idx < rentals.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>
    </Stack>
  );
};

interface RentalsPageProps {
  rentalDetailPath: (rentalId: string) => string;
}

export const RentalsPage = ({ rentalDetailPath }: RentalsPageProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') ?? '1');
  const offset = (page - 1) * RENTALS_LIMIT;

  const filters: RentalFiltersType = {
    search: searchParams.get('search') ?? '',
    status: (searchParams.get('status') as CarRentalStatus) || undefined
  };

  const { data, isPending } = useQuery(
    useCommonApi().getCarRentals({
      limit: RENTALS_LIMIT,
      offset,
      search: filters.search || undefined,
      status: filters.status
    })
  );

  const rentals = data?.items ?? [];
  const totalPages = data?.pagination?.pages ?? 0;

  const {
    data: appUserList,
    isLoading: loadingAppUserList,
    refetch: refetchAppUsers
  } = useQuery<UserResponse[]>({
    ...useCommonApi().getRegistryList(RegistryType.APP_USER_LIST),
    enabled: false
  });

  const handleFilterChange = (newFilters: RentalFiltersType) => {
    setSearchParams((params) => {
      params.set('page', '1');
      if (newFilters.search) params.set('search', newFilters.search);
      else params.delete('search');
      if (newFilters.status) params.set('status', newFilters.status);
      else params.delete('status');
      return params;
    });
  };

  const {
    handleGetAvailableRentalCars,
    isSearchingRentalCars,
    availableCars,
    setAvailableCars,
    handleRentCar,
    isProcessingRent
  } = useAvailableRentalCar();

  const {
    carRentalStatuses,
    carRentalBranches,
    rentRemoveDialogId,
    rentFinishDialogId,
    setRentRemoveDialogId,
    setRentFinishDialogId,
    isRemovingRent,
    isTakingoverRent,
    handleFinishRent,
    handleRemoveRent,
    setOpenStartRentDialogId,
    openStartRentDialogId,
    handleStartRent,
    isStartingRent
  } = useRentalCar();

  return (
    <PageLayout
      title="Autopůjčovna"
      extra={
        user?.role === Role.Dealer && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlusCircle size="20px" />}
            onClick={() => handleGetAvailableRentalCars({ rentalDate: new Date() })}
            size="large"
          >
            Nová zápůjčka
          </Button>
        )
      }
    >
      <Stack spacing={2} height={1}>
        <RentalsFilters filters={{ filters, setFilters: handleFilterChange }} disabled={isPending} />
        {isPending ? (
          <Stack width={1} alignItems="center">
            <CircularProgress color="primary" />
          </Stack>
        ) : rentals?.length ? (
          <RentalsList
            rentals={rentals}
            detailPath={rentalDetailPath}
            onRemove={(id) => setRentRemoveDialogId(id)}
            onFinish={(id) => setRentFinishDialogId(id)}
            onStart={(id) => setOpenStartRentDialogId(id)}
          />
        ) : (
          <NoDataFound />
        )}
        <FlexBlock />
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={(_, p) =>
              setSearchParams((params) => {
                params.set('page', String(p));
                return params;
              })
            }
          />
        )}
      </Stack>
      <RentStartConfirmDialog
        open={!!openStartRentDialogId}
        onClose={() => setOpenStartRentDialogId(null)}
        onAccept={handleStartRent}
        isLoading={isStartingRent}
      />
      <RentFinishConfirmDialog
        open={!!rentFinishDialogId}
        onClose={() => setRentFinishDialogId(null)}
        onAccept={handleFinishRent}
        onDeny={() => setRentFinishDialogId(null)}
        isLoading={isTakingoverRent}
        carBranches={carRentalBranches}
        carStatuses={carRentalStatuses}
      />
      <RentRemoveConfirmDialog
        open={!!rentRemoveDialogId}
        onClose={() => setRentRemoveDialogId(null)}
        onAccept={() =>
          handleRemoveRent({
            onSuccess: () => setRentRemoveDialogId(null)
          })
        }
        onDeny={() => setRentRemoveDialogId(null)}
        isLoading={isRemovingRent}
      />
      {/* Rental Cars modal */}
      <RentalCarSelectionModal
        open={!!availableCars && !isEmpty(availableCars)}
        close={() => setAvailableCars(undefined)}
        availableCars={availableCars}
        onCarSearch={handleGetAvailableRentalCars}
        isSearching={isSearchingRentalCars}
        onCarRent={(rentData, callbacks) => {
          const { onSuccess } = callbacks ?? {};

          handleRentCar(rentData, {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ['common', 'rental', 'list']
              });
              onSuccess?.();
            }
          });
        }}
        userId={Number.parseInt(user?.id ?? '')}
        isProcessingRent={isProcessingRent}
        userList={appUserList}
        isLoadingUserList={loadingAppUserList}
        fetchUserList={refetchAppUsers}
      />
    </PageLayout>
  );
};
